import { Box, Flex, Heading, Text } from "@radix-ui/themes";
import { HeroStyle2 } from "~/components/HeroStyle2";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";
import {
	Locale,
	useLocalization,
} from "~/components/localization/LocalizationProvider";
import { OptimizedImage } from "~/components/OptimizedImage";
import styles from "../live-seo-page.module.css";

type OrthoCareLocalization = {
	title: string;
	introHeading: string;
	introParagraphs: string[];
	sections: Array<{
		heading: string;
		paragraphs: string[];
		list?: string[];
		image?: {
			src: string;
			alt: string;
		};
	}>;
};

const orthoCareLocalization = {
	[Locale.EN]: {
		title: "Orthodontic Care",
		introHeading: "How to Care for Your Appliances",
		introParagraphs: [
			"Dr. Garcia-Paul is known in Miami, FL for transforming smiles.",
			"Here are her tips to care for your braces and aligners.",
		],
		sections: [
			{
				heading: "How Do I Keep My Braces Clean?",
				paragraphs: [
					"When you get your braces at Miami Orthodontics Specialists, we recommend maintaining good oral hygiene.",
					"Plaque will stick to the braces and increase the risk of cavities if good hygiene is not maintained.",
					"This can lead to swollen, bleeding gums and decalcification (white spots) on the front of the teeth.",
					"The ideal oral hygiene routine is to brush after every meal and floss at least once per day.",
					"Mechanical (power) toothbrushes can be beneficial, especially for children who don’t do as well with manual toothbrushes.",
					"Floss threaders and proxy brushes can help clean under the wire and around the gums.",
				],
			},
			{
				heading: "How Long Will My Treatment Last?",
				paragraphs: [
					"When you get your braces at Miami Orthodontic Specialists, the length of treatment is based on a combination of factors. The primary factor is the difficulty of the treatment being completed, which is based on each individual person’s bite.",
					"Dr. Garcia-Paul will give you an estimated treatment time when you come in for your consultation. However, certain things can increase time in braces such as missed appointments and breakage of appliances.",
					"To get the healthy and beautiful smile that you want, you, your family dentist, and Dr. Garcia-Paul will have to work together as a team.",
					"Proper dental care will take a little extra time and effort but the results are well worth it. It will help you enjoy the best possible smile from your orthodontic treatment.",
					"As the patient, you play the key part. It's up to you to make sure that your treatment is successful. Most importantly, you must keep your teeth clean and maintain good dental hygiene while you're wearing braces.",
				],
			},
			{
				heading: "Is There Any Way to Make the Braces Less Noticeable?",
				paragraphs: [
					"Yes! Our office offers several cosmetic options so that you can obtain the smile of your dreams without showing metal every time you talk.",
					"Options include clear braces, lingual braces and Invisalign for teens or adults. Schedule a free consultation so that we can help you select the treatment that is best for you.",
				],
			},
			{
				heading: "Plaque Is Your Enemy",
				paragraphs: [
					"Plaque is a sticky, colorless film that collects on your teeth. It's made of bacteria, food, and saliva. If you let plaque and trapped food stay on your and and around your braces, they can cause swollen gums, bad breath, cavities, and permanent marks on your teeth.",
					"As your Miami, FL orthodontist, we advise brushing your teeth regularly to remove this plaque. We teach all of our patients the correct way to floss while wearing braces to make sure you remove all plaque on a regular basis.",
				],
			},
			{
				heading: "When to Clean",
				paragraphs: [
					"Brush thoroughly each time you have a meal or snack. If you can't brush right away after a meal, be sure to at least rinse your mouth well with water until you can brush.",
					"Carry a travel toothbrush so that you can brush when you aren’t home. At least once every day, brush your teeth and braces thoroughly until they are spotlessly clean, and then floss. This takes a little extra time, so you may want to do it at night before going to bed.",
				],
			},
			{
				heading: "How to Brush",
				image: {
					src: "/watermark_24-10-2018_01-16-14PM-1024x678.jpg",
					alt: "Girl with braces smiling in a mirror, wearing pink shutter sunglasses, in a bright outdoor setting, reflecting the confidence and joy of orthodontic treatment at Miami Orthodontic Specialists.",
				},
				paragraphs: [
					"During your braces treatment at Miami Orthodontic Specialists, you will need to relearn the basics of brushing your teeth.",
					"Relearning this everyday task will teach you to pay attention to the cracks and crevices not only between teeth but around brackets and wires.",
					"Keep these tips in mind when brushing your teeth with braces:",
				],
				list: [
					"Use a fluoride toothpaste and a soft, rounded-bristle toothbrush that’s in good condition. Because braces wear out a toothbrush quickly, replace your brush as soon as it shows signs of wear.",
					"Brush around all the parts of your braces and every surface of your teeth - fronts, sides and backs. Be sure to brush your tongue and the roof of your mouth. A good way to tell if you’re brushing correctly is if your braces look clean and shiny and you can see the edges of the brackets clearly.",
					"Brush your gums gently and thoroughly.",
					"Rinse thoroughly after brushing.",
					"Inspect your teeth and braces carefully to make sure they are spotless. Look closely in a well-lighted mirror. This is a good time to check for loose or broken brackets. If you find a problem, contact Miami Orthodontic Specialists to see if it needs to be checked out and if we need to schedule a time to make a repair.",
				],
			},
			{
				heading: "How to Floss",
				paragraphs: [
					"Our helpful Miami, FL orthodontist staff will help you learn proper flossing techniques when you choose us for your orthodontic care.",
					"These tips for flossing with braces will make the process smoother for you, and you'll be a pro in no time.",
				],
				list: [
					"Floss every night before you go to bed. That way, you won’t feel rushed.",
					"When flossing with braces on, it might be necessary to use what’s called a floss threader. This reusable tool allows you to get dental floss underneath your archwire easily.",
					"Make sure you clean carefully along and under the gumlines.",
					"Flossing with braces on may seem like a difficult process, but it’s very important. And as with anything, a little practice will make it go a lot faster.",
				],
			},
			{
				heading: "Other Hygiene Helpers",
				paragraphs: [
					"In addition to the regular dental hygiene tools we are familiar with like toothbrushes and floss, there are a few products we can recommend that make brushing teeth with braces a little easier to manage.",
				],
				list: [
					"Interproximal Brush: This tool slips under your archwire to more completely remove plaque and food particles near your brackets.",
					"Prescription Fluoride Toothpaste or Fluoride Rinse: Use once a day as part of your cleaning routine, if prescribed by Dr. Garcia-Paul. For the best results, use with your interproximal brush to deliver maximum fluoride protection to your teeth.",
					"Power Toothbrush or Waterpik: These devices, such as the Oral-B Professional Series toothbrush, are designed to make brushing easier and more efficient.",
				],
			},
			{
				heading: "Problems Caused By Poor Oral Hygiene",
				paragraphs: [
					"Good dental hygiene is critical during orthodontic treatment. Without it, plaque and food can accumulate around your braces.",
					"The bacteria in plaque react with sugars and starches in food and form an acid that can eat away the enamel on your teeth, leading to white marks, cavities or gum disease.",
					"Early gum disease is reversible with professional help and good home care, but if you ignore it, it can get worse.",
					"Gum disease is usually painless, so you need to pay attention to signs like bleeding, or swollen and puffy gums.",
					"Carefully follow Dr. Garcia-Paul and staff's directions on hygiene to avoid these problems.",
				],
				list: [
					"If plaque accumulates around your braces, it can leave permanent stains on your teeth called decalcification. Lines and spots from decalcification will remain on your teeth for life.",
					"Periodontal disease, caused by the build-up of plaque, occurs in three stages. In the first stage, plaque accumulation irritates the gums. Your gums may be puffy or swollen. They may bleed when you brush or floss. This is called gingivitis.",
					"Over time, the build-up of plaque may harden into a substance called tartar. As tartar accumulates, gaps or pockets may form between your gums and teeth. Even more tartar can collect in these pockets. This is called periodontitis.",
					"Pockets of bacteria form and deepen beneath your gums, attacking and destroying the bone that anchors your teeth. This can even cause healthy teeth to loosen or eventually fall out. This is called advanced periodontitis.",
				],
			},
		],
	},
	[Locale.ES]: {
		title: "Cuidado de ortodoncia",
		introHeading: "Cómo cuidar tus aparatos",
		introParagraphs: [
			"La Dra. Garcia-Paul es conocida en Miami, FL por transformar sonrisas.",
			"Estos son sus consejos para cuidar tus brackets y alineadores.",
		],
		sections: [
			{
				heading: "¿Cómo mantengo limpios mis brackets?",
				paragraphs: [
					"Cuando recibes tus brackets en Miami Orthodontics Specialists, recomendamos mantener una buena higiene oral.",
					"La placa se adhiere a los brackets y aumenta el riesgo de caries si no se mantiene una buena higiene.",
					"Esto puede causar encías inflamadas y sangrantes, además de descalcificación (manchas blancas) en la parte frontal de los dientes.",
					"La rutina ideal de higiene oral es cepillarse después de cada comida y usar hilo dental al menos una vez al día.",
					"Los cepillos mecánicos o eléctricos pueden ser útiles, especialmente para niños que no se cepillan tan bien con un cepillo manual.",
					"Los enhebradores de hilo dental y los cepillos interproximales pueden ayudar a limpiar debajo del alambre y alrededor de las encías.",
				],
			},
			{
				heading: "¿Cuánto durará mi tratamiento?",
				paragraphs: [
					"Cuando recibes tus brackets en Miami Orthodontic Specialists, la duración del tratamiento se basa en una combinación de factores. El factor principal es la dificultad del tratamiento, que depende de la mordida de cada persona.",
					"La Dra. Garcia-Paul te dará un tiempo estimado de tratamiento cuando vengas a tu consulta. Sin embargo, ciertas cosas pueden aumentar el tiempo con brackets, como faltar a citas o romper aparatos.",
					"Para obtener la sonrisa saludable y hermosa que deseas, tú, tu dentista familiar y la Dra. Garcia-Paul tendrán que trabajar en equipo.",
					"El cuidado dental adecuado tomará un poco más de tiempo y esfuerzo, pero los resultados valen la pena. Te ayudará a disfrutar la mejor sonrisa posible de tu tratamiento de ortodoncia.",
					"Como paciente, tú desempeñas el papel principal. Depende de ti asegurarte de que tu tratamiento sea exitoso. Lo más importante es mantener tus dientes limpios y una buena higiene dental mientras usas brackets.",
				],
			},
			{
				heading: "¿Hay alguna forma de hacer que los brackets se noten menos?",
				paragraphs: [
					"Sí. Nuestro consultorio ofrece varias opciones estéticas para que puedas obtener la sonrisa de tus sueños sin mostrar metal cada vez que hablas.",
					"Las opciones incluyen brackets transparentes, brackets linguales e Invisalign para adolescentes o adultos. Programa una consulta gratuita para que podamos ayudarte a elegir el tratamiento más adecuado para ti.",
				],
			},
			{
				heading: "La placa es tu enemiga",
				paragraphs: [
					"La placa es una película pegajosa e incolora que se acumula en los dientes. Está compuesta por bacterias, comida y saliva. Si permites que la placa y los restos de comida permanezcan en tus dientes y alrededor de tus brackets, pueden causar encías inflamadas, mal aliento, caries y marcas permanentes en los dientes.",
					"Como tu ortodoncista en Miami, FL, recomendamos cepillarte regularmente para eliminar esta placa. Enseñamos a todos nuestros pacientes la forma correcta de usar hilo dental con brackets para asegurarnos de que eliminen la placa con regularidad.",
				],
			},
			{
				heading: "Cuándo limpiar",
				paragraphs: [
					"Cepíllate muy bien cada vez que comas o tomes un refrigerio. Si no puedes cepillarte inmediatamente después de una comida, al menos enjuágate bien la boca con agua hasta que puedas cepillarte.",
					"Lleva un cepillo de dientes de viaje para poder cepillarte cuando no estés en casa. Al menos una vez al día, cepilla tus dientes y brackets hasta que queden completamente limpios, y luego usa hilo dental. Esto toma un poco más de tiempo, por lo que quizá prefieras hacerlo por la noche antes de dormir.",
				],
			},
			{
				heading: "Cómo cepillarte",
				image: {
					src: "/watermark_24-10-2018_01-16-14PM-1024x678.jpg",
					alt: "Niña con brackets sonriendo en un espejo con lentes rosados, en un ambiente luminoso al aire libre.",
				},
				paragraphs: [
					"Durante tu tratamiento con brackets en Miami Orthodontic Specialists, tendrás que volver a aprender los conceptos básicos del cepillado.",
					"Volver a aprender esta tarea diaria te enseñará a prestar atención a los espacios no solo entre los dientes, sino también alrededor de los brackets y alambres.",
					"Ten presentes estos consejos cuando te cepilles los dientes con brackets:",
				],
				list: [
					"Usa pasta dental con flúor y un cepillo de cerdas suaves y redondeadas que esté en buen estado. Como los brackets desgastan rápidamente el cepillo, reemplázalo tan pronto como muestre señales de desgaste.",
					"Cepilla alrededor de todas las partes de tus brackets y todas las superficies de tus dientes: frente, lados y parte posterior. Asegúrate de cepillar también la lengua y el paladar. Una buena forma de saber si te estás cepillando correctamente es que tus brackets se vean limpios y brillantes y puedas ver claramente los bordes de los brackets.",
					"Cepilla tus encías con suavidad y de manera completa.",
					"Enjuágate bien después de cepillarte.",
					"Inspecciona cuidadosamente tus dientes y brackets para asegurarte de que estén impecables. Mira de cerca en un espejo con buena luz. Este también es un buen momento para revisar si hay brackets sueltos o rotos. Si encuentras un problema, comunícate con Miami Orthodontic Specialists para saber si debe revisarse y si necesitamos programar una reparación.",
				],
			},
			{
				heading: "Cómo usar hilo dental",
				paragraphs: [
					"Nuestro atento equipo de ortodoncia en Miami, FL te ayudará a aprender técnicas adecuadas de hilo dental cuando nos elijas para tu cuidado de ortodoncia.",
					"Estos consejos para usar hilo dental con brackets harán que el proceso sea más sencillo, y pronto lo dominarás.",
				],
				list: [
					"Usa hilo dental todas las noches antes de acostarte. Así no sentirás prisa.",
					"Al usar hilo dental con brackets, puede ser necesario usar un enhebrador de hilo dental. Esta herramienta reutilizable permite pasar fácilmente el hilo por debajo del arco.",
					"Asegúrate de limpiar cuidadosamente a lo largo y debajo de la línea de las encías.",
					"Usar hilo dental con brackets puede parecer difícil, pero es muy importante. Como con cualquier cosa, un poco de práctica hará que sea mucho más rápido.",
				],
			},
			{
				heading: "Otras ayudas de higiene",
				paragraphs: [
					"Además de las herramientas de higiene dental habituales, como cepillos e hilo dental, podemos recomendar algunos productos que facilitan el cepillado con brackets.",
				],
				list: [
					"Cepillo interproximal: esta herramienta se desliza debajo del arco para retirar mejor la placa y los restos de comida cerca de los brackets.",
					"Pasta dental con flúor recetada o enjuague con flúor: úsalo una vez al día como parte de tu rutina de limpieza, si la Dra. Garcia-Paul lo indica. Para obtener mejores resultados, úsalo con tu cepillo interproximal para llevar la máxima protección de flúor a tus dientes.",
					"Cepillo eléctrico o Waterpik: estos dispositivos, como el cepillo Oral-B Professional Series, están diseñados para hacer que el cepillado sea más fácil y eficiente.",
				],
			},
			{
				heading: "Problemas causados por una mala higiene oral",
				paragraphs: [
					"Una buena higiene dental es fundamental durante el tratamiento de ortodoncia. Sin ella, la placa y la comida pueden acumularse alrededor de tus brackets.",
					"Las bacterias de la placa reaccionan con los azúcares y almidones de los alimentos y forman un ácido que puede desgastar el esmalte dental, causando manchas blancas, caries o enfermedad de las encías.",
					"La enfermedad temprana de las encías es reversible con ayuda profesional y buen cuidado en casa, pero si se ignora puede empeorar.",
					"La enfermedad de las encías generalmente no duele, así que debes prestar atención a señales como sangrado o encías inflamadas e hinchadas.",
					"Sigue cuidadosamente las indicaciones de higiene de la Dra. Garcia-Paul y su equipo para evitar estos problemas.",
				],
				list: [
					"Si la placa se acumula alrededor de tus brackets, puede dejar manchas permanentes en tus dientes llamadas descalcificación. Las líneas y manchas de descalcificación permanecerán en tus dientes de por vida.",
					"La enfermedad periodontal, causada por la acumulación de placa, ocurre en tres etapas. En la primera etapa, la acumulación de placa irrita las encías. Las encías pueden estar inflamadas o hinchadas y pueden sangrar al cepillarte o usar hilo dental. Esto se llama gingivitis.",
					"Con el tiempo, la acumulación de placa puede endurecerse y convertirse en una sustancia llamada sarro. A medida que el sarro se acumula, pueden formarse espacios o bolsas entre las encías y los dientes. Aún más sarro puede acumularse en esas bolsas. Esto se llama periodontitis.",
					"Se forman bolsas de bacterias que se profundizan debajo de las encías, atacando y destruyendo el hueso que sostiene los dientes. Esto incluso puede hacer que dientes sanos se aflojen o eventualmente se caigan. Esto se llama periodontitis avanzada.",
				],
			},
		],
	},
} satisfies Record<Locale, OrthoCareLocalization>;

export function OrthoCarePage() {
	const getLocalization = useLocalization(orthoCareLocalization);

	return (
		<Flex direction="column">
			<HeroStyle2
				heroImageUrl="/miami-ortho-10.jpg"
				title={getLocalization("title")}
				backgroundPosition="center"
			/>
			<article className={styles.orthoCareArticle}>
				<Heading as="h2" className={styles.orthoCareMainHeading}>
					{getLocalization("introHeading")}
				</Heading>
				{getLocalization("introParagraphs").map((paragraph) => (
					<Text as="p" className={styles.orthoCareCopy} key={paragraph}>
						{paragraph}
					</Text>
				))}
				{getLocalization("sections").map((section) => (
					<section className={styles.orthoCareSection} key={section.heading}>
						<Heading as="h3" className={styles.orthoCareHeading}>
							{section.heading}
						</Heading>
						{section.image ? (
							<OptimizedImage
								src={section.image.src}
								alt={section.image.alt}
								width={1024}
								height={678}
								className={styles.orthoCareImage}
							/>
						) : null}
						{section.paragraphs.map((paragraph) => (
							<Text as="p" className={styles.orthoCareCopy} key={paragraph}>
								{paragraph}
							</Text>
						))}
						{section.list ? (
							<ul className={styles.orthoCareList}>
								{section.list.map((item) => (
									<li key={item}>{item}</li>
								))}
							</ul>
						) : null}
					</section>
				))}
			</article>
			<Box className={styles.formWrap}>
				<HomeConsultForm />
			</Box>
		</Flex>
	);
}
